/* ==========================================================================
   Smoosh Collection (v2) — card behaviors
   - Swatch picker (image swap + selected-variant tracking)
   - Multi-option tray (opens on Add for 2-option products)
   - Add-to-cart (re-renders cart drawer via same flow as PR #32)
   - Mobile education carousel dots
   ========================================================================== */
(function () {
  'use strict';

  if (window.__smooshCollectionLoaded) return;
  window.__smooshCollectionLoaded = true;

  const CART_ADD_URL = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function addVariantToCart(variantId, quantity) {
    const drawer = getCartDrawer();
    const fd = new FormData();
    fd.append('id', String(variantId));
    fd.append('quantity', String(quantity || 1));
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      fd.append('sections', drawer.getSectionsToRender().map(s => s.id).join(','));
      fd.append('sections_url', window.location.pathname);
    }
    return fetch(CART_ADD_URL, {
      method: 'POST',
      body: fd,
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' }
    })
      .then(r => r.json())
      .then(response => {
        if (response.status) {
          console.warn('[smoosh-coll] add error', response);
          return response;
        }
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.classList.remove('is-empty');
          drawer.renderContents(response);
        }
        try {
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'smoosh-collection',
              productVariantId: String(variantId),
              cartData: response
            });
          }
        } catch (_e) { /* non-fatal */ }
        return response;
      })
      .catch(err => console.warn('[smoosh-coll] add failed', err));
  }

  function flashAdded(btn, originalLabel, ms) {
    btn.classList.add('is-added');
    btn.textContent = 'Added ✓';
    setTimeout(() => {
      btn.classList.remove('is-added');
      btn.textContent = originalLabel;
    }, ms || 1300);
  }

  /* ---- Swatch picker: updates card's selected variant + image ---- */
  function selectSwatch(card, swatch) {
    if (!card || !swatch || swatch.disabled) return;
    const variantId = swatch.getAttribute('data-variant-id');
    const variantImg = swatch.getAttribute('data-variant-img');
    const variantPrice = swatch.getAttribute('data-variant-price');
    if (!variantId) return;

    card.setAttribute('data-selected-variant-id', variantId);

    card.querySelectorAll('[data-smoosh-swatch]').forEach(s => {
      s.classList.toggle('is-active', s === swatch);
    });

    if (variantImg) {
      const img = card.querySelector('[data-smoosh-card-img]');
      if (img) {
        // Clear srcset BEFORE updating src — otherwise the browser may resolve
        // the old srcset against the new src and load a stale candidate. Set
        // to empty (don't removeAttribute) so a later swatch click can set it
        // again if we ever emit per-variant srcsets.
        img.srcset = '';
        img.src = variantImg;
      }
    }

    // No price-on-button per design spec — swatch click just updates the
    // selected variant + image; the CTA copy stays "Add to Bag".
  }

  /* ---- Tray open / close ---- */
  function openTray(card) {
    const tray = card.querySelector('[data-smoosh-tray]');
    if (!tray) return;
    tray.hidden = false;
    // Two-step show: hidden -> displayed (block via [hidden]display:block rule),
    // then add .is-open next frame so the translateY transition runs.
    requestAnimationFrame(() => tray.classList.add('is-open'));
  }
  function closeTray(card) {
    const tray = card.querySelector('[data-smoosh-tray]');
    if (!tray) return;
    tray.classList.remove('is-open');
    setTimeout(() => { tray.hidden = true; }, 320);
  }

  /* ---- Tray option select ---- */
  function selectTrayOpt(tray, opt) {
    if (!tray || !opt) return;
    tray.querySelectorAll('[data-smoosh-tray-opt]').forEach(o => {
      const on = o === opt;
      o.classList.toggle('is-sel', on);
      o.setAttribute('aria-checked', on ? 'true' : 'false');
    });
  }

  /* ---- Variant matrix (emitted as <script type="application/json"> by the
         card snippet). Cached per-card on first read. ---- */
  function getVariantMatrix(card) {
    if (card.__smooshVariantMatrix) return card.__smooshVariantMatrix;
    const script = card.querySelector('script[data-smoosh-card-variants]');
    if (!script) return (card.__smooshVariantMatrix = []);
    try {
      card.__smooshVariantMatrix = JSON.parse(script.textContent);
    } catch (_e) {
      card.__smooshVariantMatrix = [];
    }
    return card.__smooshVariantMatrix;
  }

  /* ---- Resolve a variant ID. Single-option: return what the swatch (or
         initial first-available) selected. Multi-option: match (option1 from
         active swatch) + (option2 from selected tray opt) against the
         variant matrix. ---- */
  function resolveVariantId(card) {
    const optionsCount = parseInt(card.getAttribute('data-options-count') || '1', 10);
    const selected = card.getAttribute('data-selected-variant-id');
    if (optionsCount < 2) return selected;

    const activeSwatch = card.querySelector('[data-smoosh-swatch].is-active');
    const selectedTrayOpt = card.querySelector('[data-smoosh-tray-opt].is-sel');
    if (!activeSwatch || !selectedTrayOpt) return selected;

    const variants = getVariantMatrix(card);
    if (!variants.length) return selected;

    const swatchVariantId = parseInt(activeSwatch.getAttribute('data-variant-id'), 10);
    const swatchVariant = variants.find(v => v.id === swatchVariantId);
    if (!swatchVariant) return selected;

    const o1Value = swatchVariant.o1;
    const o2Value = selectedTrayOpt.getAttribute('data-second-value');
    const match = variants.find(v => v.o1 === o1Value && v.o2 === o2Value);
    return match ? String(match.id) : selected;
  }

  function onClick(e) {
    // Swatch
    const swatch = e.target.closest('[data-smoosh-swatch]');
    if (swatch && !swatch.disabled) {
      e.preventDefault();
      const card = swatch.closest('[data-smoosh-card]');
      selectSwatch(card, swatch);
      return;
    }

    // Tray option
    const trayOpt = e.target.closest('[data-smoosh-tray-opt]');
    if (trayOpt) {
      e.preventDefault();
      const tray = trayOpt.closest('[data-smoosh-tray]');
      selectTrayOpt(tray, trayOpt);
      return;
    }

    // Tray cancel
    if (e.target.closest('[data-smoosh-tray-cancel]')) {
      e.preventDefault();
      const card = e.target.closest('[data-smoosh-card]');
      if (card) closeTray(card);
      return;
    }

    // Tray confirm
    if (e.target.closest('[data-smoosh-tray-confirm]')) {
      e.preventDefault();
      const card = e.target.closest('[data-smoosh-card]');
      if (!card) return;
      const variantId = resolveVariantId(card);
      if (!variantId) return;
      const addBtn = card.querySelector('[data-smoosh-add]');
      const originalLabel = addBtn ? addBtn.textContent : '';
      closeTray(card);
      addVariantToCart(variantId, 1).finally(() => {
        if (addBtn) flashAdded(addBtn, originalLabel, 1400);
      });
      return;
    }

    // Add button
    const addBtn = e.target.closest('[data-smoosh-add]');
    if (!addBtn || addBtn.disabled) return;
    e.preventDefault();
    const card = addBtn.closest('[data-smoosh-card]');
    if (!card) return;

    const optionsCount = parseInt(card.getAttribute('data-options-count') || '1', 10);
    if (optionsCount >= 2) {
      openTray(card);
      return;
    }
    const variantId = card.getAttribute('data-selected-variant-id');
    if (!variantId) return;

    const originalLabel = addBtn.textContent;
    addBtn.classList.add('is-loading');
    addBtn.disabled = true;
    addVariantToCart(variantId, 1).finally(() => {
      addBtn.classList.remove('is-loading');
      addBtn.disabled = false;
      flashAdded(addBtn, originalLabel, 1300);
    });
  }

  /* ---- Mobile education carousel: update dots on scroll ---- */
  function wireEduCarousel(edu) {
    if (!edu) return;
    const grid = edu.querySelector('.smoosh-coll-edu__grid');
    const dots = edu.querySelectorAll('.smoosh-coll-edu__dot');
    if (!grid || dots.length === 0) return;
    const update = () => {
      if (window.innerWidth >= 750) return;
      const cardWidth = grid.scrollWidth / dots.length;
      const idx = Math.round(grid.scrollLeft / cardWidth);
      dots.forEach((d, i) => d.classList.toggle('is-on', i === Math.min(idx, dots.length - 1)));
    };
    grid.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  document.addEventListener('click', onClick);
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.smoosh-coll-edu').forEach(wireEduCarousel);
  });
  // Fire immediately if DOM already ready (defer-loaded script after DCL).
  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    document.querySelectorAll('.smoosh-coll-edu').forEach(wireEduCarousel);
  }
})();
